import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';
import MushinLogo from '@/components/MushinLogo';

const RED = '#E02D45';

const navLinks = [
  { name: 'Products', path: '/products/factory-os' },
  { name: 'Industries', path: '/#industries' },
  { name: 'Manufacturing 4.0', path: '/manufacturing-4' },
  { name: 'Security & Deployment', path: '/security' },
];

export default function Footer() {
  return (
    <footer className="bg-[#060606] border-t border-white/[0.06]" data-testid="main-footer">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-14 lg:gap-20">

          {/* LEFT — Brand + Address */}
          <div>
            <MushinLogo size="large" />
            <p className="mt-5 text-[13px] text-white/35 max-w-xs leading-[1.7] font-medium">
              The digital backbone of manufacturing. Factory operations, supplier governance, and Industry 4.0 in one connected platform.
            </p>

            <div className="mt-6 flex items-start gap-2.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: RED }} strokeWidth={2} />
              <p className="text-[13px] text-white/45 font-medium leading-snug">
                Sector 28, DLF Phase 1, Gurugram
              </p>
            </div>

            <div className="mt-5 flex items-center gap-2.5">
              <svg width="20" height="14" viewBox="0 0 20 14" className="flex-shrink-0 rounded-[2px]" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <rect width="20" height="4.67" fill="#FF9933" />
                <rect y="4.67" width="20" height="4.67" fill="#FFFFFF" />
                <rect y="9.33" width="20" height="4.67" fill="#138808" />
                <circle cx="10" cy="7" r="1.8" fill="none" stroke="#000080" strokeWidth="0.4" />
              </svg>
              <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-white/30">
                Made in India. Made for India.
              </p>
            </div>
          </div>

          {/* CENTER — Navigation */}
          <div className="md:flex md:justify-center">
            <div>
              <h4 className="text-[11px] font-bold text-white/20 mb-6 uppercase tracking-[0.2em]">Navigation</h4>
              <ul className="space-y-3.5">
                {navLinks.map(l => (
                  <li key={l.path}>
                    <Link to={l.path} className="text-[13px] text-white/40 hover:text-white transition-colors font-medium" data-testid={`footer-link-${l.name.toLowerCase().replace(/[\s&\/]+/g, '-')}`}>
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* RIGHT — Contact */}
          <div className="md:flex md:justify-end">
            <div>
              <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-6" style={{ color: RED }}>Contact</h4>

              <div className="space-y-4">
                <a href="tel:+919599907773" className="flex items-center gap-3 group" data-testid="footer-phone">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Phone className="w-3.5 h-3.5" style={{ color: RED }} strokeWidth={2} />
                  </div>
                  <span className="text-[14px] font-bold text-white/70 group-hover:text-white transition-colors">
                    +91 95999 07773
                  </span>
                </a>

                <a href="mailto:sales@mushinlabs.com" className="flex items-center gap-3 group" data-testid="footer-email-sales">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Mail className="w-3.5 h-3.5" style={{ color: RED }} strokeWidth={2} />
                  </div>
                  <span className="text-[13px] font-medium text-white/50 group-hover:text-white transition-colors">
                    sales@mushinlabs.com
                  </span>
                </a>

                <a href="mailto:hello@mushinlabs.com" className="flex items-center gap-3 group" data-testid="footer-email-hello">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Mail className="w-3.5 h-3.5" style={{ color: RED }} strokeWidth={2} />
                  </div>
                  <span className="text-[13px] font-medium text-white/50 group-hover:text-white transition-colors">
                    hello@mushinlabs.com
                  </span>
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact"
                  className="text-white px-6 py-2.5 rounded-full text-[13px] font-bold transition-all duration-300 hover:shadow-lg inline-flex items-center"
                  style={{ backgroundColor: RED }}
                  data-testid="footer-book-demo">
                  Book a Demo
                </Link>
                <a href="mailto:sales@mushinlabs.com"
                  className="border border-white/[0.1] hover:border-white/20 text-white/50 hover:text-white px-6 py-2.5 rounded-full text-[13px] font-bold transition-colors"
                  data-testid="footer-get-quote">
                  Get a Quote
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="mt-14 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[12px] text-white/15 font-medium">
            &copy; {new Date().getFullYear()} Mushin Technologies Private Limited. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-[12px] text-white/15 hover:text-white/35 transition-colors font-medium">Privacy Policy</a>
            <a href="#" className="text-[12px] text-white/15 hover:text-white/35 transition-colors font-medium">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
